import { Component, Input, OnChanges } from '@angular/core';
import { Basket } from '@app/models/basket';
import { ChargeTypes } from '@app/models/charge-types';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { BasketService } from '@app/api/basket.service';
import { IssueTypes } from '@app/models/issue-types';
import { Charge } from '@app/models/Charge';
import { MenuService } from '@app/api/menu.service';
import { BasketCharge } from '@app/models/basket/basket-charge';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'app-basket-charges',
  styleUrls: ['./basket-charges.component.scss'],
  templateUrl: './basket-charges.component.html',
  imports: [CurrencyPipe]
})
export class BasketChargesComponent extends AndroWebCoreComponent implements OnChanges {
  @Input() public basket: Basket;
  @Input() public orderComplete: boolean;
  @Input() public showErrorsOnly: boolean;
  @Input() public basketTotal: number = 0;
  @Input() public showTotal: boolean = true;

  public charges: BasketCharge[];
  public basketIssues: string[] = [];

  constructor(private menuService: MenuService, private basketService: BasketService, private _currencyPipe: CurrencyPipe) {
    super();
  }

  ngOnChanges(): void {
    this.setBasketCharges();
  }

  /**
   * Sets the visible basket charges and applies filtering logic.
   * Also triggers issue validation.
   */
  private setBasketCharges(): void {
    if (!this.basket) {
      return;
    }

    const displayedCharges: Charge[] = this.getDisplayedCharges();
    this.charges = this.isOnlySubtotal(displayedCharges)
      ? []
      : displayedCharges.map((charge: Charge) => this.mapToChargeDisplay(charge)).filter((charge: BasketCharge) => this.shouldIncludeCharge(charge));

    this.checkBasketIssues();
  }

  /**
   * Gets charges that should be displayed to the user.
   * Filters based on a predefined list of visible charge types.
   * @returns An array of filtered `Charge` objects
   */
  private getDisplayedCharges(): Charge[] {
    const typesToShow: ChargeTypes[] = [
      ChargeTypes.Delivery,
      ChargeTypes.MinimumSpendSurcharge,
      ChargeTypes.SubTotalIncludingTax,
      ChargeTypes.ServiceCharge,
      ChargeTypes.LoyaltyPointsDiscount,
      ChargeTypes.Discount
    ];

    return this.basket.Charges.filter((charge) => typesToShow.includes(charge.ChargeType));
  }

  /**
   * Checks if only the subtotal charge is present (used to suppress display).
   * @param charges - The array of charges to evaluate
   * @returns `true` if subtotal is the only charge and greater than zero
   */
  private isOnlySubtotal(charges: Charge[]): boolean {
    return charges.length === 1 && charges[0].ChargeType === ChargeTypes.SubTotalIncludingTax && charges[0].Price.Amount > 0;
  }

  /**
   * Maps a `Charge` to a `BasketCharge` object with display-friendly values.
   * @param charge - The charge to map
   * @returns A `BasketCharge` object
   */
  private mapToChargeDisplay(charge: Charge): BasketCharge {
    return {
      narrative: this.getFriendlyNarrative(charge.ChargeType, charge.Narrative),
      price: charge.Price.Amount,
      onDelete: this.getOnDelete(charge.ChargeType),
      type: charge.ChargeType
    };
  }

  /**
   * Gets a user-friendly narrative for a given charge type.
   * @param type - The `ChargeTypes` value
   * @param fallback - The default narrative to return if not customized
   * @returns A friendly label for the charge
   */
  private getFriendlyNarrative(type: ChargeTypes, fallback: string): string {
    switch (type) {
      case ChargeTypes.SubTotalIncludingTax:
        return 'Subtotal';
      case ChargeTypes.LoyaltyPointsDiscount:
        return 'Loyalty Discount';
      default:
        return fallback;
    }
  }

  /**
   * Returns a callback to remove a charge if applicable (e.g. discounts or loyalty).
   * @param type - The type of charge
   * @returns A function to execute on delete or `undefined` if not removable
   */
  private getOnDelete(type: ChargeTypes): (() => void) | undefined {
    switch (type) {
      case ChargeTypes.LoyaltyPointsDiscount:
        return () => this.removeLoyalty();
      case ChargeTypes.Discount:
        return () => this.removeVoucher();
      default:
        return undefined;
    }
  }

  /**
   * Determines whether a charge should be included in the view.
   * Applies special logic for discount visibility.
   * @param charge - The charge to test
   * @returns `true` if the charge should be included
   */
  private shouldIncludeCharge(charge: { type: ChargeTypes }): boolean {
    if (charge.type === ChargeTypes.Discount) {
      return !!this.basket.VoucherCode && (!this.basket.UnlockedProductIds || this.basket.UnlockedProductIds.length === 0);
    }

    return true;
  }

  /**
   * Checks for specific issues in the basket and maps them to user-friendly error messages.
   * Handles loyalty and deal constraints, filters out address/time issues, and formats others.
   */
  private async checkBasketIssues(): Promise<void> {
    if (this.basket) {
      const displayedIssues: string[] = [];

      if (this.basket.Issues?.length > 0) {
        for (const issue of JSON.parse(JSON.stringify(this.basket.Issues))) {
          if (
            issue.IssueType === IssueTypes.LoyaltyPointsWouldViolateMinimumRemainingOrderValue ||
            issue.IssueType === IssueTypes.LoyaltyPointsWouldDiscountMoreThanAllowedValue ||
            issue.IssueType === IssueTypes.LoyaltyPointsWouldDiscountMoreThanAllowedPercentage
          ) {
            this.removeLoyalty();
          }

          // don't display postcode issues since address section on checkout will handle it.
          // don't display wanted time issues since time section on checkout will handle it.
          if (
            issue.IssueType === IssueTypes.DeliveryOccasionRequiresDeliveryLocation ||
            issue.IssueType === IssueTypes.SiteDoesNotDeliverToDeliveryLocation ||
            issue.IssueType === IssueTypes.OccasionIsNotAvailableAtWantedTime ||
            issue.IssueType === IssueTypes.SiteIsNotAvailableForAsapOrdersNow ||
            issue.IssueType === IssueTypes.SiteDoesNotAcceptAsapOrdersForOccasion
          ) {
            break;
          }

          let message: string;
          if (issue.IssueType === IssueTypes.DealMinimumSpendIsLowerThanBasketSpend) {
            message = issue.Narrative;

            const deal = this.menuService.menuValue?.Deals.find((x) => x.Id === issue.ProductId);

            if (deal) {
              message = `${deal.MinimumSpend ? `${deal.MinimumSpend} minimum` : 'Minimum '} spend has not been met for deal ${deal.Name}. (excludes delivery charges)`;
            } else {
              const e: string = await this.getErrorMessage(issue.IssueType);
              message = e === 'something has gone wrong' && issue.Narrative ? issue.Narrative : e;
            }
          } else if (issue.IssueType === IssueTypes.TotalIsLessThanMinimumSpend) {
            const suffix: string = this.basket.DeliveryMinimumSpend ? `than the ${this._currencyPipe.transform(this.basket.DeliveryMinimumSpend, 'GBP')}` : `than the`;
            message = `The basket subtotal is less ${suffix} minimum required.`;
          } else {
            const e: string = await this.getErrorMessage(issue.IssueType);
            message = e === 'something has gone wrong' && issue.Narrative ? issue.Narrative : e;
          }

          displayedIssues.push(message);
        }
      }

      this.basketIssues = Array.from(new Set(displayedIssues));
    }
  }

  /**
   * Removes the voucher from the basket and updates the basket state.
   */
  private async removeVoucher(): Promise<void> {
    const response: Basket | null = await this.basketService.deleteVoucherFromBasket(this.basket.Id);

    if (response) {
      response.WantedTimeUtc = this.basket.WantedTimeUtc;
      this.basket = response;
    }
  }

  /**
   * Removes the loyalty discount by redeeming 0 points.
   */
  private removeLoyalty(): void {
    this.subscriptions$[`BasketComponent-redeemLoyalty-basketService-redeemLoyaltyOnBasket()`] = this.basketService.redeemLoyaltyOnBasket(this.basket.Id, 0).subscribe();
  }
}
